import React from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import type { ChicletTone } from 'sutra/components/Chiclet/Chiclet'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import GenericSvgIcon from 'components/GenericSvgIcon'
import {
  NavIconWrapper,
  NavLabelRow,
  NavText,
  navRowCss,
} from 'components/navigation/nav-row-styles'
import type { NavigationItem } from 'features/project/partials/project-layout/components/navigation/utils'

export type NavChicletConfig = {
  text: string
  $tone: ChicletTone
}

type NavItemLinkProps = {
  item: NavigationItem
  to: string
  isActive: boolean
  isCollapsed: boolean
  isHighlighted: boolean
  chiclet?: NavChicletConfig
  onClick: (e: React.MouseEvent) => void
}

// Pure view: the single nav row rendered for a top-level item or a group
// child. All routing/tracking decisions are made by the caller and handed in
// as `to` + `onClick`. A group child's indent comes entirely from its
// wrapper (NavGroup's ChildrenSlide) — this row looks identical either way.
export const NavItemLink: React.FC<NavItemLinkProps> = ({
  item,
  to,
  isActive,
  isCollapsed,
  isHighlighted,
  chiclet,
  onClick,
}) => (
  <Tooltip content={isCollapsed ? item.title : ''} side="right">
    <StyledLink
      $isActive={isActive}
      $isCollapsed={isCollapsed}
      $isHighlighted={isHighlighted}
      data-nav-url={item.url}
      to={to}
      onClick={onClick}
    >
      <NavIconWrapper>
        <GenericSvgIcon SvgComponent={item.svgComponent} />
      </NavIconWrapper>
      {/* Always mounted — $hidden fades the label instead of unmounting it,
          so it dissolves in sync with the row's own collapse transition
          rather than popping away. See nav-row-styles.ts. */}
      {chiclet ? (
        <NavLabelRow $hidden={isCollapsed}>
          <NavText>{item.title}</NavText>
          <Chiclet $tone={chiclet.$tone}>{chiclet.text}</Chiclet>
        </NavLabelRow>
      ) : (
        <NavText $hidden={isCollapsed}>{item.title}</NavText>
      )}
    </StyledLink>
  </Tooltip>
)

const StyledLink = styled(Link)<{
  $isActive: boolean
  $isCollapsed: boolean
  $isHighlighted?: boolean
}>`
  ${navRowCss}
`
